import React from 'react'
import { Field } from 'react-final-form'

import RadioField from '@cinch/oxd-components/field-inline-radio/src'
import SelectField from '@cinch/oxd-components/field-select/src'
import tokens from '@cinch/oxd-utils/tokens/src'

import { AnswerDataType, PageQuestionsItem, MobileInputType, QuestionControlType } from '../../types'
import validate from './field-validator'
import TextField from '@cinch/design-system/src/components/forms/field-text/field-text.component'

interface FieldFactoryProps {
  question: PageQuestionsItem
  validateFields?: string[]
}

// Temporarily overriding TextField styles for 'Sell it' journey, pending site-wide implementation of these input changes.
const CustomTextField = (props: any) => <TextField {...props} override={true} oxford={true} />

const getFieldHelpText = (question: PageQuestionsItem): string => {
  const { assumedValue, canBeAssumed, questionParameterName } = question

  if (questionParameterName === 'PreviousOwners' && canBeAssumed) {
    const isAssumed = ['0', '1', '2', '3', '4', '5+'].includes(assumedValue)

    return isAssumed ? "We've set this for you from our records." : ''
  } else {
    return ''
  }
}

const formatInt = (value: string) => {
  if (!value) {
    return value
  }

  return value.replace(/[^\d]/g, '')
}

const formatNumber = (value: string) => {
  if (!value) {
    return value
  }

  return value.replace(/[^\d.]/g, '')
}

const buildOptions = (question: PageQuestionsItem) => {
  if (
    question.questionControlType === QuestionControlType.InlineBoolRadio ||
    question.questionControlType === QuestionControlType.BoolRadio
  ) {
    return [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
    ]
  }

  const [displayContent] = question.questionsDisplayContent

  const options =
    displayContent &&
    displayContent.multipleChoiceAnswerOptions.map((opt) => ({
      label: opt.optionDisplayText,
      value: opt.optionValue.toString(),
    }))

  return options || []
}

const getFieldInfo = (question: PageQuestionsItem): string[] => {
  let placeholder = question.questionParameterName
  let label = question.isRequired ? placeholder : `${placeholder} (optional)`

  const [displayContent] = question.questionsDisplayContent
  if (displayContent) {
    placeholder = displayContent.questionText || displayContent.formFieldLabel
    label = question.isRequired ? placeholder : `${placeholder} (optional)`
  }

  return [placeholder, label]
}

const getFieldOptions = (question: PageQuestionsItem) => {
  const fieldOptions = {} as any
  const { questionControlType, mobileInputType, answerDataType } = question

  if (questionControlType !== QuestionControlType.TextEntry) {
    return fieldOptions
  }
  if (mobileInputType !== MobileInputType.Text) {
    fieldOptions.type = mobileInputType === MobileInputType.Email ? 'email' : 'tel'
  }
  if (answerDataType === AnswerDataType.Int) {
    fieldOptions.parse = formatInt
  }
  if (answerDataType === AnswerDataType.Number) {
    fieldOptions.parse = formatNumber
  }

  return fieldOptions
}

const fieldComponent = (questionControlType: QuestionControlType) => {
  const mql = (typeof window !== 'undefined' && window.matchMedia(tokens.customMedia.m)) || { matches: false }

  switch (questionControlType) {
    case QuestionControlType.TextEntry:
      return CustomTextField
    case QuestionControlType.ListDropdown:
      return SelectField
    case QuestionControlType.ListRadio:
    case QuestionControlType.InlineBoolRadio:
    case QuestionControlType.BoolRadio:
      return mql.matches ? RadioField : SelectField
    default:
      return CustomTextField
  }
}

// Return a Field from a wbac question object
const FieldFactory: React.FC<FieldFactoryProps> = ({ question, validateFields = [] }) => {
  if (question.isHidden) {
    return null
  }

  const [placeholder, label] = getFieldInfo(question)

  return (
    <Field
      component={fieldComponent(question.questionControlType)}
      helpText={getFieldHelpText(question)}
      label={label}
      name={question.questionParameterName}
      options={buildOptions(question)}
      placeholder={placeholder}
      validate={validate(question)}
      validateFields={validateFields}
      {...getFieldOptions(question)}
    />
  )
}

export default FieldFactory
